import React, { useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';

interface PropertyAnalysis {
  totalProperties: number;
  duplicatesByListNumber: Array<{
    listNumber: string;
    count: number;
    docIds: string[];
    titles: string[];
  }>;
  duplicatesByTitle: Array<{
    title: string;
    count: number;
    docIds: string[];
  }>;
  propertiesWithoutAgency: number;
  propertiesWithoutAgent: number;
  invalidPrices: number;
  missingFields: {
    titulo: number;
    precio: number;
    direccion: number;
    agencia: number;
  };
  estimatedUniqueProperties: number;
  redundancyPercentage: number;
}

export default function PropertyAnalyzer() {
  const { user } = useUser();
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState<PropertyAnalysis | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleaningProgress, setCleaningProgress] = useState('');

  // Solo admins
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="alert alert-danger">
        <h6>Acceso Denegado</h6>
        <p>Solo administradores pueden analizar propiedades.</p>
      </div>
    );
  }

  const analyzeProperties = async () => {
    setAnalyzing(true);
    setResults(null);

    try {
      console.log('🏠 Analizando propiedades...');

      // Obtener todas las propiedades
      const propSnapshot = await getDocs(collection(db, 'propiedades'));
      const propiedades = propSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      })) as any[];

      console.log(`📊 Total propiedades encontradas: ${propiedades.length}`);

      // 1. Analizar duplicados por list_number
      const listNumberMap = new Map<string, any[]>();
      propiedades.forEach(prop => {
        const listNum = prop.list_number;
        if (listNum) {
          if (!listNumberMap.has(listNum)) {
            listNumberMap.set(listNum, []);
          }
          listNumberMap.get(listNum)!.push(prop);
        }
      });

      const duplicatesByListNumber = Array.from(listNumberMap.entries())
        .filter(([_, props]) => props.length > 1)
        .map(([listNumber, props]) => ({
          listNumber,
          count: props.length,
          docIds: props.map(p => p.id),
          titles: props.map(p => p.titulo || p.nombre || 'Sin título')
        }))
        .sort((a, b) => b.count - a.count);

      // 2. Analizar duplicados por título
      const titleMap = new Map<string, any[]>();
      propiedades.forEach(prop => {
        const title = (prop.titulo || prop.nombre || '').trim().toLowerCase();
        if (title && title !== 'sin título') {
          if (!titleMap.has(title)) {
            titleMap.set(title, []);
          }
          titleMap.get(title)!.push(prop);
        }
      });

      const duplicatesByTitle = Array.from(titleMap.entries())
        .filter(([_, props]) => props.length > 1)
        .map(([title, props]) => ({
          title,
          count: props.length,
          docIds: props.map(p => p.id)
        }))
        .sort((a, b) => b.count - a.count);

      // 3. Analizar campos faltantes
      let propertiesWithoutAgency = 0;
      let propertiesWithoutAgent = 0;
      let invalidPrices = 0;
      let missingTitulo = 0;
      let missingPrecio = 0;
      let missingDireccion = 0;
      let missingAgencia = 0;

      propiedades.forEach(prop => {
        // Verificar agencia
        const agenciaId = prop.agencia_info?.inmobiliaria_id || prop.inmobiliaria_id;
        if (!agenciaId) {
          propertiesWithoutAgency++;
          missingAgencia++;
        }

        // Verificar agente
        if (!prop.asesor_id || !Array.isArray(prop.asesor_id) || prop.asesor_id.length === 0) {
          propertiesWithoutAgent++;
        }

        // Verificar precio
        const precio = prop.precio_actual_usd || prop.precio_original_usd || 0;
        if (precio <= 0 || isNaN(precio)) {
          invalidPrices++;
          missingPrecio++;
        }

        // Verificar título
        if (!prop.titulo && !prop.nombre) {
          missingTitulo++;
        }

        // Verificar dirección
        if (!prop.direccion && !prop.direccion?.calle) {
          missingDireccion++;
        }
      });

      // 4. Calcular estimación de propiedades únicas
      const uniqueListNumbers = new Set(propiedades.map(p => p.list_number).filter(Boolean));
      const estimatedUniqueProperties = uniqueListNumbers.size;
      const redundancyPercentage = Math.round(((propiedades.length - estimatedUniqueProperties) / propiedades.length) * 100);

      const analysis: PropertyAnalysis = {
        totalProperties: propiedades.length,
        duplicatesByListNumber,
        duplicatesByTitle,
        propertiesWithoutAgency,
        propertiesWithoutAgent,
        invalidPrices,
        missingFields: {
          titulo: missingTitulo,
          precio: missingPrecio,
          direccion: missingDireccion,
          agencia: missingAgencia
        },
        estimatedUniqueProperties,
        redundancyPercentage
      };

      setResults(analysis);
      console.log('✅ Análisis de propiedades completado:', analysis);

    } catch (error) {
      console.error('❌ Error analizando propiedades:', error);
      Swal.fire('Error', 'No se pudo analizar las propiedades.', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const cleanDuplicatesByListNumber = async () => {
    if (!results || results.duplicatesByListNumber.length === 0) return;

    const totalDuplicatesToDelete = results.duplicatesByListNumber.reduce(
      (sum, dup) => sum + (dup.count - 1), 0
    );

    const confirm = await Swal.fire({
      title: 'Limpiar Duplicados por List Number',
      html: `
        <div class="text-start">
          <p><strong>Se eliminarán ${totalDuplicatesToDelete} propiedades duplicadas</strong></p>
          <p>Se mantendrá 1 propiedad por cada list_number único</p>
          <p><strong>Propiedades únicas resultantes:</strong> ${results.estimatedUniqueProperties}</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, limpiar duplicados',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    setCleaning(true);
    try {
      let deleted = 0;
      let total = totalDuplicatesToDelete;

      for (const duplicate of results.duplicatesByListNumber) {
        // Mantener solo el primer documento, eliminar el resto
        const docsToDelete = duplicate.docIds.slice(1);
        
        for (const docId of docsToDelete) {
          setCleaningProgress(`Eliminando duplicado ${deleted + 1}/${total}...`);
          await deleteDoc(doc(db, 'propiedades', docId));
          deleted++;
        }
      }

      Swal.fire({
        title: 'Limpieza Completada',
        html: `
          <div class="text-start">
            <p><strong>✅ ${deleted} propiedades duplicadas eliminadas</strong></p>
            <p><strong>📊 Propiedades restantes:</strong> ~${results.estimatedUniqueProperties}</p>
            <p><strong>🎯 Reducción:</strong> ${results.redundancyPercentage}% de duplicados eliminados</p>
          </div>
        `,
        icon: 'success'
      });

      // Re-analizar después de limpieza
      await analyzeProperties();

    } catch (error) {
      console.error('Error limpiando duplicados:', error);
      Swal.fire('Error', 'No se pudieron eliminar todos los duplicados.', 'error');
    } finally {
      setCleaning(false);
      setCleaningProgress('');
    }
  };

  const getRecommendation = () => {
    if (!results) return '';

    if (results.redundancyPercentage > 60) {
      return '🚨 CRÍTICO: Recomendado re-migración completa de propiedades';
    } else if (results.redundancyPercentage > 30) {
      return '⚠️ ALTO: Recomendada limpieza de duplicados';
    } else if (results.redundancyPercentage > 10) {
      return '🔧 MEDIO: Limpieza opcional para optimización';
    } else {
      return '✅ BAJO: Nivel de duplicados aceptable';
    }
  };

  return (
    <div className="property-analyzer">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-home me-2"></i>
            Análisis de Consistencia - Propiedades
          </h5>
        </div>
        <div className="card-body">
          
          {!results && (
            <div className="text-center">
              <p className="text-muted mb-4">
                Este análisis detectará duplicados, inconsistencias y problemas 
                en la colección de propiedades para determinar si se requiere limpieza o re-migración.
              </p>
              <button
                className="btn btn-primary btn-lg"
                onClick={analyzeProperties}
                disabled={analyzing}
              >
                {analyzing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Analizando propiedades...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    Analizar Propiedades
                  </>
                )}
              </button>
            </div>
          )}

          {cleaning && (
            <div className="alert alert-warning text-center">
              <div className="spinner-border text-warning mb-3" role="status"></div>
              <p className="mb-0">{cleaningProgress}</p>
            </div>
          )}

          {results && (
            <div className="property-analysis-results">
              {/* Resumen Ejecutivo */}
              <div className="alert alert-info">
                <h6><i className="fas fa-chart-bar me-2"></i>Resumen de Análisis</h6>
                <div className="row">
                  <div className="col-md-3">
                    <strong>Total Propiedades:</strong><br />
                    <span className="fs-4 text-primary">{results.totalProperties.toLocaleString()}</span>
                  </div>
                  <div className="col-md-3">
                    <strong>Únicas Estimadas:</strong><br />
                    <span className="fs-4 text-success">{results.estimatedUniqueProperties.toLocaleString()}</span>
                  </div>
                  <div className="col-md-3">
                    <strong>Duplicados:</strong><br />
                    <span className="fs-4 text-danger">{(results.totalProperties - results.estimatedUniqueProperties).toLocaleString()}</span>
                  </div>
                  <div className="col-md-3">
                    <strong>% Redundancia:</strong><br />
                    <span className={`fs-4 ${results.redundancyPercentage > 50 ? 'text-danger' : results.redundancyPercentage > 25 ? 'text-warning' : 'text-success'}`}>
                      {results.redundancyPercentage}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Recomendación */}
              <div className={`alert ${results.redundancyPercentage > 50 ? 'alert-danger' : results.redundancyPercentage > 25 ? 'alert-warning' : 'alert-success'}`}>
                <h6><i className="fas fa-lightbulb me-2"></i>Recomendación</h6>
                <p className="mb-0">{getRecommendation()}</p>
              </div>

              {/* Estadísticas Detalladas */}
              <div className="row mb-4">
                <div className="col-md-2">
                  <div className="card text-center bg-danger text-white">
                    <div className="card-body">
                      <h4>{results.duplicatesByListNumber.length}</h4>
                      <p className="mb-0">List Numbers Duplicados</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card text-center bg-warning text-white">
                    <div className="card-body">
                      <h4>{results.duplicatesByTitle.length}</h4>
                      <p className="mb-0">Títulos Duplicados</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card text-center bg-info text-white">
                    <div className="card-body">
                      <h4>{results.propertiesWithoutAgency}</h4>
                      <p className="mb-0">Sin Agencia</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card text-center bg-secondary text-white">
                    <div className="card-body">
                      <h4>{results.propertiesWithoutAgent}</h4>
                      <p className="mb-0">Sin Agente</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card text-center bg-dark text-white">
                    <div className="card-body">
                      <h4>{results.invalidPrices}</h4>
                      <p className="mb-0">Precios Inválidos</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card text-center bg-primary text-white">
                    <div className="card-body">
                      <h4>{Object.values(results.missingFields).reduce((a, b) => a + b, 0)}</h4>
                      <p className="mb-0">Campos Faltantes</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Duplicados por List Number */}
              {results.duplicatesByListNumber.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6><i className="fas fa-copy me-2"></i>Duplicados por List Number (Top 10)</h6>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={cleanDuplicatesByListNumber}
                      disabled={cleaning}
                    >
                      <i className="fas fa-broom me-1"></i>
                      Limpiar Todos
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>List Number</th>
                            <th>Duplicados</th>
                            <th>Títulos</th>
                            <th>IDs de Documentos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.duplicatesByListNumber.slice(0, 10).map((dup, index) => (
                            <tr key={index}>
                              <td><code>{dup.listNumber}</code></td>
                              <td><span className="badge bg-danger">{dup.count}</span></td>
                              <td>
                                <small className="text-muted">
                                  {dup.titles.slice(0, 2).join(', ')}
                                  {dup.titles.length > 2 && `... (+${dup.titles.length - 2})`}
                                </small>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {dup.docIds.slice(0, 2).join(', ')}
                                  {dup.docIds.length > 2 && `... (+${dup.docIds.length - 2})`}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Campos Faltantes */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6><i className="fas fa-exclamation-triangle me-2"></i>Campos Faltantes</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-warning">{results.missingFields.titulo}</h5>
                        <p className="mb-0">Sin Título</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-danger">{results.missingFields.precio}</h5>
                        <p className="mb-0">Sin Precio</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-info">{results.missingFields.direccion}</h5>
                        <p className="mb-0">Sin Dirección</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-secondary">{results.missingFields.agencia}</h5>
                        <p className="mb-0">Sin Agencia</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Acciones Recomendadas */}
              <div className="card">
                <div className="card-header">
                  <h6><i className="fas fa-cogs me-2"></i>Acciones Disponibles</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-success">Limpieza Selectiva</h6>
                      <p className="small">Eliminar solo duplicados, mantener datos únicos</p>
                      <button
                        className="btn btn-outline-success w-100"
                        onClick={cleanDuplicatesByListNumber}
                        disabled={cleaning || results.duplicatesByListNumber.length === 0}
                      >
                        <i className="fas fa-broom me-2"></i>
                        Limpiar Duplicados ({results.duplicatesByListNumber.reduce((sum, d) => sum + (d.count - 1), 0)})
                      </button>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-danger">Re-migración Completa</h6>
                      <p className="small">Eliminar todas las propiedades y re-migrar desde CSV</p>
                      <button
                        className="btn btn-outline-danger w-100"
                        onClick={() => alert('Esta acción requiere confirmación adicional. Use la herramienta de migración.')}
                      >
                        <i className="fas fa-database me-2"></i>
                        Preparar Re-migración
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}